import React from 'react';
import i18n from 'i18n';
import { addDays } from 'date-fns';
import { Input } from 'modules/core/components/Input/Input';
import { FormField, IField } from 'modules/core/types';
import { CalculatorAdornment, DollarAdornment, PercentAdornment, Toggle } from 'modules/core/components';
import { ColumnWidth } from 'modules/core/enums';
import {
  requiredValidator,
  validateMinRequiredValue,
  validateMinRequiredValueForDropdown,
  validateNumberValue,
  validateRequiredMinimumValue,
} from 'modules/core/validators';
import { MONEY_SYMBOL } from 'modules/core/constants';
import { CalendarInput } from 'modules/core/components/CalendarInput';
import { COLORS } from 'modules/core/theme/colors';

import { LOAN_VARIABLES_FIELDS_NAMES } from './fieldNames.const';
import { DecisionCommentsNote } from '../components/common/DecisionCommentsNote';
import { ConfirmCheckBox } from '../components/common/ConfirmCheckBox/ConfirmCheckBox';
import { LabelField } from '../components/common/Label/LabelField';
import { Dropdown } from '../components/common/Dropdown/Dropdown';
import { DEFAULT_LOAN_VARIABLES_VALUES } from './consts';

/** Holds a FormRow's label line without printing anything -- FormRow skips a falsy label. */
const NON_BREAKING_SPACE = '\u00A0';

/** Pins each cell of Ally's third row to the same 20% the five-column rows above use. */
const ALLY_THIRD_ROW_COLUMN = 'col-20';

export const FIRST_MW_ROW_FIELDS: IField[] = [
  {
    label: i18n.t('components.loanVariablesModal.fields.retailPrice'),
    name: LOAN_VARIABLES_FIELDS_NAMES.RETAIL_PRICE,
    component: Input,
    controlProps: {
      fieldWidth: ColumnWidth.FULL,
      textPosition: 'start',
      decimalScale: 0,
      required: true,
      startAdornment: <DollarAdornment />,
      rules: requiredValidator,
      validate: validateRequiredMinimumValue,
      placeholder: i18n.t('components.input.placeholders.integer'),
      hideInitZeroValue: true,
      labelWithoutAsterisk: true,
    },
  },
  {
    label: i18n.t('components.loanVariablesModal.fields.cashDown'),
    name: LOAN_VARIABLES_FIELDS_NAMES.CASH_DOWN,
    component: Input,
    controlProps: {
      required: true,
      startAdornment: <DollarAdornment />,
      fieldWidth: ColumnWidth.FULL,
      decimalScale: 0,
      placeholder: i18n.t('components.input.placeholders.integer'),
      hideInitZeroValue: true,
      labelWithoutAsterisk: true,
    },
  },
  {
    name: LOAN_VARIABLES_FIELDS_NAMES.NET_TRADE_IN,
    component: Input,
    label: i18n.t('components.loanVariablesModal.fields.netTradeIn'),
    isVerticalAlign: true,
    controlProps: {
      required: true,
      allowNegative: true,
      readonly: true,
      startAdornment: <DollarAdornment />,
      textPosition: 'start',
      endAdornment: <CalculatorAdornment />,
      decimalScale: 3,
      hideInitZeroValue: true,
      placeholder: i18n.t('components.input.placeholders.integer'),
      labelWithoutAsterisk: true,
    },
  },
  {
    label: i18n.t('components.loanVariablesModal.fields.rebates'),
    name: LOAN_VARIABLES_FIELDS_NAMES.REBATES,
    component: Input,
    controlProps: {
      required: true,
      startAdornment: <DollarAdornment />,
      fieldWidth: ColumnWidth.FULL,
      decimalScale: 0,
      placeholder: i18n.t('components.input.placeholders.integer'),
      hideInitZeroValue: true,
      labelWithoutAsterisk: true,
    },
  },
  {
    label: i18n.t('components.loanVariablesModal.fields.loanTermWithNumber'),
    name: LOAN_VARIABLES_FIELDS_NAMES.LOAN_TERM,
    component: Dropdown,
    controlProps: {
      fieldWidth: ColumnWidth.FULL,
      required: true,
      rules: requiredValidator,
      validate: validateMinRequiredValueForDropdown,
      labelWithoutAsterisk: true,
    },
  },
];

export const SECOND_MW_ROW_FIELDS: IField[] = [
  {
    name: LOAN_VARIABLES_FIELDS_NAMES.MILEAGE,
    component: Input,
    label: i18n.t('components.loanVariablesModal.fields.mileage'),
    isVerticalAlign: true,
    controlProps: {
      textPosition: 'start',
      required: true,
      decimalScale: 0,
      rules: requiredValidator,
      maxLength: 7,
      validate: validateNumberValue,
      placeholder: i18n.t('components.input.placeholders.integer'),
      labelWithoutAsterisk: true,
    },
  },
  {
    name: LOAN_VARIABLES_FIELDS_NAMES.GUIDEBOOK_VALUE,
    component: Input,
    label: i18n.t('components.loanVariablesModal.fields.jdpTradeIn'),
    isVerticalAlign: true,
    controlProps: {
      textPosition: 'start',
      required: true,
      decimalScale: 0,
      rules: requiredValidator,
      validate: validateRequiredMinimumValue,
      startAdornment: <DollarAdornment />,
      placeholder: i18n.t('components.input.placeholders.integer'),
      hideInitZeroValue: true,
      labelWithoutAsterisk: true,
    },
  },
  {
    name: LOAN_VARIABLES_FIELDS_NAMES.TOTAL_SALES_FEES,
    component: Input,
    label: i18n.t('components.loanVariablesModal.fields.salesFees'),
    isVerticalAlign: true,
    controlProps: {
      readonly: true,
      textPosition: 'start',
      endAdornment: <CalculatorAdornment />,
      required: true,
      decimalScale: 0,
      startAdornment: <DollarAdornment />,
      rules: requiredValidator,
      hideInitZeroValue: true,
      placeholder: i18n.t('components.input.placeholders.integer'),
      labelWithoutAsterisk: true,
    },
  },
  {
    label: i18n.t('components.loanVariablesModal.fields.taxes'),
    name: LOAN_VARIABLES_FIELDS_NAMES.TAXES,
    component: Input,
    controlProps: {
      fieldWidth: ColumnWidth.FULL,
      required: true,
      rules: requiredValidator,
      validate: validateNumberValue,
      decimalScale: 0,
      startAdornment: <DollarAdornment />,
      placeholder: i18n.t('components.input.placeholders.integer'),
      labelWithoutAsterisk: true,
    },
  },
  {
    label: i18n.t('components.loanTermTable.columns.participation'),
    name: LOAN_VARIABLES_FIELDS_NAMES.PARTICIPATION,
    component: Input,
    controlProps: {
      fieldWidth: ColumnWidth.FULL,
      required: true,
      rules: requiredValidator,
      decimalScale: 2,
      endAdornment: <PercentAdornment />,
      validate: validateNumberValue,
      placeholder: i18n.t('components.input.placeholders.decimal'),
      hideInitZeroValue: true,
      labelWithoutAsterisk: true,
    },
  },
];

const VSC_FIELD: IField = {
  label: i18n.t('components.loanVariablesModal.fields.vscWithNumber'),
  name: LOAN_VARIABLES_FIELDS_NAMES.VSC,
  component: Input,
  controlProps: {
    required: true,
    fieldWidth: ColumnWidth.FULL,
    decimalScale: 0,
    startAdornment: <DollarAdornment />,
    placeholder: i18n.t('components.input.placeholders.integer'),
    hideInitZeroValue: true,
    labelWithoutAsterisk: true,
  },
};

const ADD_ONS_FIELD: IField = {
  label: i18n.t('components.loanVariablesModal.fields.addOns'),
  name: LOAN_VARIABLES_FIELDS_NAMES.ADD_ONS,
  component: Input,
  controlProps: {
    required: true,
    fieldWidth: ColumnWidth.FULL,
    decimalScale: 0,
    startAdornment: <DollarAdornment />,
    placeholder: i18n.t('components.input.placeholders.integer'),
    hideInitZeroValue: true,
    labelWithoutAsterisk: true,
  },
};

const GAP_FIELD: IField = {
  label: i18n.t('components.loanVariablesModal.fields.gapWithNumber'),
  name: LOAN_VARIABLES_FIELDS_NAMES.GAP,
  component: Input,
  controlProps: {
    required: true,
    fieldWidth: ColumnWidth.FULL,
    decimalScale: 0,
    startAdornment: <DollarAdornment />,
    placeholder: i18n.t('components.input.placeholders.integer'),
    hideInitZeroValue: true,
    labelWithoutAsterisk: true,
  },
};

export const THIRD_MW_ROW_FIELDS: IField[] = [VSC_FIELD, ADD_ONS_FIELD, GAP_FIELD];

const ALLY_DECISION_COMMENTS_FIELD: IField = {
  label: NON_BREAKING_SPACE,
  name: LOAN_VARIABLES_FIELDS_NAMES.DECISION_COMMENTS,
  component: DecisionCommentsNote as FormField,
  // Full-width control rather than the inputs' col-11, to keep the note on one line.
  isVerticalAlign: true,
  controlProps: {
    fieldWidth: ColumnWidth.FULL,
    labelWithoutAsterisk: true,
    className: ALLY_THIRD_ROW_COLUMN,
  },
};

const pinnedToAllyColumn = (field: IField): IField => ({
  ...field,
  controlProps: { ...field.controlProps, className: ALLY_THIRD_ROW_COLUMN },
});

/** Front Adds, Back Adds, then the decision comments where GAP used to sit. */
export const ALLY_THIRD_MW_ROW_FIELDS: IField[] = [
  pinnedToAllyColumn(ADD_ONS_FIELD),
  pinnedToAllyColumn({
    ...VSC_FIELD,
    controlProps: {
      ...VSC_FIELD.controlProps,
      endAdornment: <CalculatorAdornment dataTestId="back-adds-calculator" />,
    },
  }),
  ALLY_DECISION_COMMENTS_FIELD,
];

export const BALLOON_FIELDS: IField[] = [
  {
    label: i18n.t('components.loanVariablesModal.fields.annualMileage'),
    name: LOAN_VARIABLES_FIELDS_NAMES.ANNUAL_MILEAGE,
    component: Dropdown,
    controlProps: {
      fieldWidth: ColumnWidth.FULL,
      required: true,
      rules: requiredValidator,
      validate: validateMinRequiredValueForDropdown,
      labelWithoutAsterisk: true,
      className: 'col-3',
      placeholder: i18n.t('components.loanVariablesModal.placeholders.annualMileage'),
    },
  },
  {
    label: i18n.t('components.loanVariablesModal.fields.engine'),
    name: LOAN_VARIABLES_FIELDS_NAMES.ENGINE,
    component: Dropdown,
    controlProps: {
      fieldWidth: ColumnWidth.FULL,
      required: true,
      labelWithoutAsterisk: true,
      className: 'col-3',
      placeholder: i18n.t('components.loanVariablesModal.placeholders.engine'),
    },
  },
];

export const PAYMENT_FIELD: IField = {
  label: i18n.t('components.loanVariablesModal.fields.approvedPayment'),
  name: LOAN_VARIABLES_FIELDS_NAMES.PAYMENT,
  component: Input,
  controlProps: {
    fieldWidth: ColumnWidth.FULL,
    decimalScale: 0,
    required: true,
    startAdornment: <DollarAdornment />,
    rules: requiredValidator,
    validate: validateRequiredMinimumValue,
    placeholder: i18n.t('components.input.placeholders.integer'),
    labelWithoutAsterisk: true,
  },
};

export const LENDER_PAYMENT_FIELD: IField = {
  label: i18n.t('components.loanVariablesModal.fields.targetPayment'),
  name: LOAN_VARIABLES_FIELDS_NAMES.PAYMENT,
  component: Input,
  controlProps: {
    fieldWidth: ColumnWidth.FULL,
    decimalScale: 0,
    required: true,
    startAdornment: <DollarAdornment />,
    rules: requiredValidator,
    validate: validateRequiredMinimumValue,
    placeholder: i18n.t('components.input.placeholders.integer'),
    labelWithoutAsterisk: true,
  },
};

export const APPLY_CPO_CHECKBOX_FIELD: IField = {
  name: LOAN_VARIABLES_FIELDS_NAMES.APPLY_CPO,
  component: ConfirmCheckBox,
  textLabel: i18n.t('components.loanVariablesModal.confirmTexts.applyCpo'),
  controlProps: {
    checkedColor: COLORS.PURPLE_1,
    // SWA-7969: the purple check stays as the CPOV accent, the label reads black.
    labelColor: COLORS.BLACK,
    lockWhenDisabled: true,
  },
};

export const CURRENT_PAYMENT_MW_LABEL: IField = {
  label: i18n.t('components.loanVariablesModal.fields.currentPaymentWithNumber'),
  name: LOAN_VARIABLES_FIELDS_NAMES.CURRENT_PAYMENT,
  component: LabelField,
  controlProps: {
    fieldWidthForce: true,
    fieldWidth: ColumnWidth.XS,
    required: true,
    labelWithoutAsterisk: true,
  },
};

export const BALLOON_PAYMENT_MW_LABEL: IField = {
  label: i18n.t('components.loanVariablesModal.fields.balloonPayment'),
  name: LOAN_VARIABLES_FIELDS_NAMES.BALLOON_PAYMENT,
  component: LabelField,
  controlProps: {
    fieldWidth: ColumnWidth.XS,
    fieldWidthForce: true,
    required: true,
    labelWithoutAsterisk: true,
  },
};

export const AFG_TRIM_FIELD: IField = {
  label: i18n.t('components.loanVariablesModal.fields.afgTrim'),
  name: LOAN_VARIABLES_FIELDS_NAMES.AFG_TRIM,
  component: Dropdown,
  controlProps: {
    fieldWidth: ColumnWidth.FULL,
    required: true,
    rules: requiredValidator,
    validate: validateMinRequiredValueForDropdown,
    labelWithoutAsterisk: true,
    placeholder: i18n.t('components.loanVariablesModal.placeholders.afgTrim'),
    autoWidth: true,
  },
};

export const TOTAL_AMOUNT_FINANCED_MW_LABEL: IField = {
  label: i18n.t('components.loanVariablesModal.fields.totalAmountFinanced'),
  name: LOAN_VARIABLES_FIELDS_NAMES.TOTAL_AMOUNT_FINANCED,
  component: LabelField,
  controlProps: {
    fieldWidth: ColumnWidth.XS,
    fieldWidthForce: true,
    required: true,
    labelWithoutAsterisk: true,
  },
};

export const FIRST_MW_ROW_LABELS: IField[] = [
  {
    label: i18n.t('components.loanVariablesModal.fields.retailPrice'),
    name: LOAN_VARIABLES_FIELDS_NAMES.RETAIL_PRICE,
    component: LabelField,
    controlProps: {
      fieldWidth: ColumnWidth.FULL,
      required: true,
      labelWithoutAsterisk: true,
      symbol: MONEY_SYMBOL,
    },
  },
  {
    label: i18n.t('components.loanVariablesModal.fields.cashDown'),
    name: LOAN_VARIABLES_FIELDS_NAMES.CASH_DOWN,
    component: LabelField,
    controlProps: {
      fieldWidth: ColumnWidth.FULL,
      required: true,
      labelWithoutAsterisk: true,
    },
  },
  {
    name: LOAN_VARIABLES_FIELDS_NAMES.NET_TRADE_IN,
    component: LabelField,
    label: i18n.t('components.loanVariablesModal.fields.netTradeIn'),
    isVerticalAlign: true,
    controlProps: {
      fieldWidth: ColumnWidth.FULL,
      required: true,
      labelWithoutAsterisk: true,
    },
  },
  {
    label: i18n.t('components.loanVariablesModal.fields.rebates'),
    name: LOAN_VARIABLES_FIELDS_NAMES.REBATES,
    component: LabelField,
    controlProps: {
      fieldWidth: ColumnWidth.FULL,
      required: true,
      labelWithoutAsterisk: true,
    },
  },
  {
    label: i18n.t('components.loanVariablesModal.fields.loanTermWithNumber'),
    name: LOAN_VARIABLES_FIELDS_NAMES.LOAN_TERM,
    component: LabelField,
    controlProps: {
      fieldWidth: ColumnWidth.FULL,
      required: true,
      labelWithoutAsterisk: true,
      isNeedFormat: false,
    },
  },
];

export const SECOND_MW_ROW_LABELS: IField[] = [
  {
    name: LOAN_VARIABLES_FIELDS_NAMES.MILEAGE,
    component: LabelField,
    label: i18n.t('components.loanVariablesModal.fields.mileage'),
    isVerticalAlign: true,
    controlProps: {
      fieldWidth: ColumnWidth.FULL,
      required: true,
      labelWithoutAsterisk: true,
      isNeedFormat: false,
    },
  },
  {
    name: LOAN_VARIABLES_FIELDS_NAMES.GUIDEBOOK_VALUE,
    component: LabelField,
    label: i18n.t('components.loanVariablesModal.fields.jdpTradeIn'),
    isVerticalAlign: true,
    controlProps: {
      fieldWidth: ColumnWidth.FULL,
      required: true,
      labelWithoutAsterisk: true,
    },
  },
  {
    name: LOAN_VARIABLES_FIELDS_NAMES.TOTAL_SALES_FEES,
    component: LabelField,
    label: i18n.t('components.loanVariablesModal.fields.salesFees'),
    isVerticalAlign: true,
    controlProps: {
      fieldWidth: ColumnWidth.FULL,
      required: true,
      labelWithoutAsterisk: true,
    },
  },
  {
    label: i18n.t('components.loanVariablesModal.fields.taxes'),
    name: LOAN_VARIABLES_FIELDS_NAMES.TAXES,
    component: LabelField,
    controlProps: {
      fieldWidth: ColumnWidth.FULL,
      required: true,
      labelWithoutAsterisk: true,
    },
  },
  {
    label: i18n.t('components.loanTermTable.columns.participation'),
    name: LOAN_VARIABLES_FIELDS_NAMES.PARTICIPATION,
    component: LabelField,
    controlProps: {
      fieldWidth: ColumnWidth.FULL,
      required: true,
      labelWithoutAsterisk: true,
      isNeedFormat: false,
    },
  },
];

const VSC_LABEL: IField = {
  label: i18n.t('components.loanVariablesModal.fields.vscWithNumber'),
  name: LOAN_VARIABLES_FIELDS_NAMES.VSC,
  component: LabelField,
  controlProps: {
    fieldWidth: ColumnWidth.FULL,
    required: true,
    labelWithoutAsterisk: true,
  },
};

const ADD_ONS_LABEL: IField = {
  label: i18n.t('components.loanVariablesModal.fields.addOns'),
  name: LOAN_VARIABLES_FIELDS_NAMES.ADD_ONS,
  component: LabelField,
  controlProps: {
    fieldWidth: ColumnWidth.FULL,
    required: true,
    labelWithoutAsterisk: true,
  },
};

const GAP_LABEL: IField = {
  label: i18n.t('components.loanVariablesModal.fields.gapWithNumber'),
  name: LOAN_VARIABLES_FIELDS_NAMES.GAP,
  component: LabelField,
  controlProps: {
    fieldWidth: ColumnWidth.FULL,
    required: true,
    labelWithoutAsterisk: true,
  },
};

export const THIRD_MW_ROW_LABELS: IField[] = [VSC_LABEL, ADD_ONS_LABEL, GAP_LABEL];

/** Print view of the Ally row above: same two amounts, same order, no GAP. */
export const ALLY_THIRD_MW_ROW_LABELS: IField[] = [ADD_ONS_LABEL, VSC_LABEL];

export const ALLY_LINE_3_MW_LABEL: IField = {
  label: i18n.t('components.loanVariablesModal.fields.lineThree'),
  name: LOAN_VARIABLES_FIELDS_NAMES.LINE_THREE,
  component: LabelField,
  controlProps: {
    fieldWidth: ColumnWidth.XS,
    fieldWidthForce: true,
    required: true,
    labelWithoutAsterisk: true,
  },
};

export const ALLY_TOTAL_AMOUNT_FINANCED_MW_LABEL: IField = {
  label: i18n.t('components.loanVariablesModal.fields.totalAmountFinanced'),
  name: LOAN_VARIABLES_FIELDS_NAMES.TOTAL_AMOUNT_FINANCED,
  component: LabelField,
  controlProps: {
    fieldWidth: ColumnWidth.XS,
    fieldWidthForce: true,
    required: true,
    labelWithoutAsterisk: true,
  },
};

export const BALLOON_LABELS: IField[] = [
  {
    label: i18n.t('components.loanVariablesModal.fields.annualMileage'),
    name: LOAN_VARIABLES_FIELDS_NAMES.ANNUAL_MILEAGE,
    component: LabelField,
    controlProps: {
      fieldWidth: ColumnWidth.FULL,
      required: true,
      labelWithoutAsterisk: true,
      className: 'col-3',
      isNeedFormat: false,
    },
  },
  {
    label: i18n.t('components.loanVariablesModal.fields.engine'),
    name: LOAN_VARIABLES_FIELDS_NAMES.ENGINE,
    component: LabelField,
    controlProps: {
      fieldWidth: ColumnWidth.FULL,
      required: true,
      labelWithoutAsterisk: true,
      className: 'col-3',
      isNeedFormat: false,
    },
  },
];

export const MONTHLY_INCOME_LABEL: IField = {
  label: i18n.t('components.loanVariablesModal.fields.monthlyIncome'),
  name: LOAN_VARIABLES_FIELDS_NAMES.MONTHLY_INCOME,
  component: LabelField,
  controlProps: {
    fieldWidth: ColumnWidth.FULL,
    required: true,
    labelWithoutAsterisk: true,
  },
};

export const CREATE_ADDENDUM_FIELD: IField = {
  label: i18n.t('components.loanVariablesModal.fields.createAddendum'),
  name: LOAN_VARIABLES_FIELDS_NAMES.CREATE_ADDENDUM,
  component: Toggle as FormField,
  controlProps: {
    fieldWidth: ColumnWidth.XL,
    fieldWidthForce: true,
    required: true,
    labelWithoutAsterisk: true,
    icons: false,
    showLabels: true,
  },
};

export const SUBMIT_DEAL_FIELD: IField = {
  label: i18n.t('components.loanVariablesModal.fields.submitDeal'),
  name: LOAN_VARIABLES_FIELDS_NAMES.AFG_SUBMIT_DEAL,
  component: Toggle as FormField,
  controlProps: {
    fieldWidth: ColumnWidth.XL,
    fieldWidthForce: true,
    required: true,
    labelWithoutAsterisk: true,
    icons: false,
    showLabels: true,
  },
};

export const CONTRACT_DATE_FIELD: IField = {
  label: i18n.t('components.loanVariablesModal.fields.contractDate'),
  name: LOAN_VARIABLES_FIELDS_NAMES.CONTRACT_DATE,
  component: CalendarInput,
  controlProps: {
    fieldWidth: ColumnWidth.FULL,
    fieldWidthForce: true,
    required: true,
    labelWithoutAsterisk: true,
    icons: false,
    showLabels: true,
    validate: validateMinRequiredValue,
    get minDate() {
      return DEFAULT_LOAN_VARIABLES_VALUES.loanVariablesForm.contractDate;
    },
    get maxDate() {
      return addDays(DEFAULT_LOAN_VARIABLES_VALUES.loanVariablesForm.contractDate, 30);
    },
  },
};

export const FIRST_PAYMENT_DATE_FIELD: IField = {
  label: i18n.t('components.loanVariablesModal.fields.firstPaymentDate'),
  name: LOAN_VARIABLES_FIELDS_NAMES.FIRST_PAYMENT_DATE,
  component: CalendarInput,
  controlProps: {
    fieldWidth: ColumnWidth.FULL,
    fieldWidthForce: true,
    required: true,
    labelWithoutAsterisk: true,
    icons: false,
    showLabels: true,
    validate: validateMinRequiredValue,
  },
};

export const PAYMENT_LABEL: IField = {
  label: i18n.t('components.loanVariablesModal.fields.approvedPayment'),
  name: LOAN_VARIABLES_FIELDS_NAMES.PAYMENT,
  component: LabelField,
  controlProps: {
    fieldWidth: ColumnWidth.FULL,
    required: true,
    labelWithoutAsterisk: true,
  },
};

export const LENDER_PAYMENT_LABEL: IField = {
  label: i18n.t('components.loanVariablesModal.fields.targetPayment'),
  name: LOAN_VARIABLES_FIELDS_NAMES.PAYMENT,
  component: LabelField,
  controlProps: {
    fieldWidth: ColumnWidth.FULL,
    required: true,
    labelWithoutAsterisk: true,
  },
};

export const LENDER_MW_ROUTE_ONE_USER_ID_FIELD: IField = {
  name: LOAN_VARIABLES_FIELDS_NAMES.ROUTE_ONE_USER_ID,
  component: Input,
  label: i18n.t('components.loanVariablesModal.confirmTexts.routeOneUserId.label'),
  controlProps: {
    required: true,
    fieldWidth: ColumnWidth.FULL,
    valueAsNumber: false,
    maxLength: 50,
    placeholderWithoutAsterisk: true,
    labelWithoutAsterisk: true,
  },
};

export const MONTHLY_INCOME_FIELD: IField = {
  label: i18n.t('components.loanVariablesModal.fields.monthlyIncome'),
  name: LOAN_VARIABLES_FIELDS_NAMES.MONTHLY_INCOME,
  component: Input,
  controlProps: {
    fieldWidth: ColumnWidth.FULL,
    startAdornment: <DollarAdornment />,
    textPosition: 'start',
    endAdornment: <CalculatorAdornment />,
    decimalScale: 0,
    readonly: true,
    placeholder: i18n.t('components.input.placeholders.integer'),
    required: true,
    rules: requiredValidator,
    validate: validateRequiredMinimumValue,
    labelWithoutAsterisk: true,
  },
};
